import { Link } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import { PageHeader, QueryState } from '../components/page';
import { useIdentityAiStatus, useIdentityOverview, useStartIdentityTriage } from '../api/hooks';
import type { IdentityLevel, IdentityOverview } from '../api/types';
import {
  DataQualityNav,
  LEVEL_NAME,
  findingKindText,
  identityErrorText,
  loadError,
} from './medical-data-quality';

const LEVELS: IdentityLevel[] = ['organisation', 'site', 'department', 'person'];
const TRIAGE_LIMIT = 50;

export function DataQualityHeader() {
  return (
    <>
      <PageHeader
        title="Medical data quality"
        description="Organisations, Catalog Sites, Departments and Investigators the Medical Repository resolved from CTIS site rows, with the links it could not settle on its own."
      />
      <DataQualityNav />
    </>
  );
}

export function agreementText(agreement: IdentityOverview['ai_agreement']): string {
  const since = new Date(agreement.since).toLocaleDateString();
  if (agreement.compared === 0 || agreement.rate === null)
    return `No reviewer decision since ${since} had an AI suggestion to compare.`;
  return `The AI agreed with reviewers on ${agreement.agreed} of ${agreement.compared} Link Candidates (${Math.round(agreement.rate * 100)}%) since ${since}.`;
}

function Stat({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="rounded-3xl border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value.toLocaleString()}</div>
      {note && <div className="mt-1 text-xs text-muted-foreground">{note}</div>}
    </div>
  );
}

function AiTriage() {
  const status = useIdentityAiStatus();
  const triage = useStartIdentityTriage();
  if (!status.data?.enabled) return null;
  const start = async () => {
    try {
      const result = await triage.mutateAsync(TRIAGE_LIMIT);
      toast.success(
        `Queued ${result.queued} Link Candidates for AI triage. Suggestions appear in the review queue.`
      );
    } catch (error) {
      toast.error(identityErrorText(error, 'Could not start AI triage'));
    }
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <Button size="sm" disabled={triage.isPending} onClick={() => void start()}>
        <Sparkles className="h-4 w-4" />
        Start AI triage
      </Button>
      <span className="text-xs text-muted-foreground">
        {status.data.model ?? 'Approved model'}
        {status.data.last_run_at
          ? ` · last run ${new Date(status.data.last_run_at).toLocaleString()}`
          : ' · not run yet'}
      </span>
    </div>
  );
}

export function DataQualityOverviewPage() {
  const overview = useIdentityOverview();
  const data = overview.data;
  const openCandidates = data
    ? LEVELS.reduce((sum, level) => sum + (data.open_candidates[level] ?? 0), 0)
    : 0;
  const findings = Object.entries(data?.open_findings ?? {}).sort((a, b) => b[1] - a[1]);
  const openFindings = findings.reduce((sum, [, count]) => sum + count, 0);

  return (
    <div>
      <DataQualityHeader />
      <QueryState isLoading={overview.isLoading} error={loadError(overview.error)}>
        {data === null ? (
          <p role="status" className="text-sm text-muted-foreground">
            This Rudi release does not offer medical data review yet.
          </p>
        ) : data ? (
          <div className="space-y-6">
            <section
              aria-label="Resolved entities"
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              {LEVELS.map(level => (
                <Stat
                  key={level}
                  label={LEVEL_NAME[level].many}
                  value={data.entities[level] ?? 0}
                  note={`${data.open_candidates[level] ?? 0} open Link Candidates`}
                />
              ))}
            </section>
            <section className="grid gap-3 sm:grid-cols-3">
              <Stat
                label="Mentions"
                value={data.mentions}
                note={`${data.organisation_only_mentions} link to an Organisation only`}
              />
              <Link to="/data-quality/review" className="block">
                <Stat label="Open Link Candidates" value={openCandidates} note="Review queue" />
              </Link>
              <Link to="/data-quality/findings" className="block">
                <Stat label="Open Findings" value={openFindings} note="Findings" />
              </Link>
            </section>
            <section aria-label="AI agreement" className="rounded-3xl border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold">AI suggestions</h2>
                  <p className="mt-1 text-sm">{agreementText(data.ai_agreement)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Suggestions are review input. Only reviewers change links.
                  </p>
                </div>
                <AiTriage />
              </div>
            </section>
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Open Findings by kind</h2>
              {findings.length === 0 ? (
                <p className="text-sm text-muted-foreground">No open Findings.</p>
              ) : (
                <ul className="divide-y rounded-3xl border">
                  {findings.map(([kind, count]) => (
                    <li key={kind} className="flex justify-between px-4 py-2 text-sm">
                      <span>{findingKindText(kind)}</span>
                      <span className="font-medium">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}
